import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { ApiError, api } from '../../lib/api';
import { errorMessage } from '../../lib/errors';

export interface Blocker {
  id: string;
  title: string;
  status: string;
}

/**
 * Cambio de estado de una tarea (tablero y detalle). Si la tarea depende de otras sin terminar, la API lo rechaza con
 * 409 (bloqueo duro, guía 7.3) y aquí quedan los bloqueos para mostrarlos. Si era recurrente, devuelve la siguiente.
 */
export function useTaskStatus() {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [blocked, setBlocked] = useState<{ taskId: string; blockers: Blocker[] } | null>(null);
  const [next, setNext] = useState<{ id: string; dueDate: string } | null>(null);

  async function change(taskId: string, statusId: string): Promise<boolean> {
    setBusy(true);
    setError(null);
    setBlocked(null);
    setNext(null);
    try {
      const res = await api<{ next: { id: string; dueDate: string } | null }>(`/api/tareas/${taskId}/estado`, { json: { statusId } });
      setNext(res.next);
      await Promise.all([qc.invalidateQueries({ queryKey: ['proyectos'] }), qc.invalidateQueries({ queryKey: ['tareas'] })]);
      return true;
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { code?: string; blockers?: Blocker[] } | undefined) : undefined;
      if (err instanceof ApiError && err.status === 409 && body?.code === 'blocked_by_dependencies') setBlocked({ taskId, blockers: body.blockers ?? [] });
      else setError(errorMessage(err));
      return false;
    } finally {
      setBusy(false);
    }
  }

  return { change, busy, error, blocked, next };
}
